// ============================================================
// THE COMPANY — Action Items checklist for a role
// ============================================================

import { useState } from "react";
import { useCompanyStore } from "../../store/useCompanyStore";

const EMPTY_ITEMS: import("../../lib/types").ActionItem[] = [];

export default function ActionItems({ roleId }: { roleId: string }) {
  const items = useCompanyStore((s) => s.actionItems[roleId] ?? EMPTY_ITEMS);
  const addActionItem = useCompanyStore((s) => s.addActionItem);
  const updateActionItem = useCompanyStore((s) => s.updateActionItem);
  const deleteActionItem = useCompanyStore((s) => s.deleteActionItem);
  const [newText, setNewText] = useState("");

  const pendingCount = items.filter((i) => !i.completed).length;

  const handleAdd = async () => {
    const text = newText.trim();
    if (!text) return;
    setNewText("");
    await addActionItem(roleId, text);
  };

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <h3
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: "var(--text-muted)" }}
        >
          Action Items
        </h3>
        {pendingCount > 0 && (
          <span
            className="inline-flex items-center justify-center h-4 min-w-[16px] rounded-full px-1 text-[10px] font-bold"
            style={{
              backgroundColor: "rgba(99, 102, 241, 0.15)",
              color: "var(--accent)",
            }}
          >
            {pendingCount}
          </span>
        )}
      </div>

      {/* Items list */}
      {items.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="action-item-row flex items-start gap-2 rounded-md px-2 py-1.5 -mx-2"
              style={{ backgroundColor: "transparent" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--bg-elevated)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {/* Custom Checkbox */}
              <input
                type="checkbox"
                checked={item.completed}
                onChange={() =>
                  updateActionItem(item.id, roleId, {
                    completed: !item.completed,
                  })
                }
                className="action-checkbox"
              />

              {/* Text */}
              <span
                className="action-item-text flex-1 text-xs leading-relaxed"
                style={{
                  color: item.completed
                    ? "var(--text-muted)"
                    : "var(--text-secondary)",
                  textDecoration: item.completed ? "line-through" : "none",
                }}
              >
                {item.text}
              </span>

              {/* Delete */}
              <button
                type="button"
                onClick={() => deleteActionItem(item.id, roleId)}
                className="action-item-delete shrink-0 h-5 w-5 flex items-center justify-center rounded cursor-pointer"
                style={{
                  color: "var(--text-muted)",
                  border: "none",
                  backgroundColor: "transparent",
                }}
                title="Delete"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="2" y1="2" x2="12" y2="12" />
                  <line x1="12" y1="2" x2="2" y2="12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && (
        <div className="empty-state mb-3">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
          <p>No action items yet</p>
        </div>
      )}

      {/* Add input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
          placeholder="Add action item..."
          className="flex-1 rounded-md border px-2.5 py-1.5 text-xs outline-none transition-colors duration-150 focus:border-[var(--accent)]"
          style={{
            backgroundColor: "var(--bg-base)",
            borderColor: "var(--border-default)",
            color: "var(--text-primary)",
          }}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newText.trim()}
          className="rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors duration-150 disabled:opacity-40"
          style={{
            backgroundColor: "var(--accent)",
            color: "#ffffff",
            border: "none",
          }}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.backgroundColor = "var(--accent-hover)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "var(--accent)";
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}
